import React from "react";
import { cn } from "@/lib/utils";
import type { SimpleFieldConfig } from "../../types";

export interface TableCellWrapperProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  cellIndex: number;
  totalCells: number;
  isTextareaField: boolean;
  columnStyle?: React.CSSProperties;
  children: React.ReactNode;
  /** Column label, surfaced to the cell-aware row context menu. */
  fieldLabel?: string;
  /**
   * Stringified primitive cell value for "Filter by this value". Omitted for
   * non-primitive values (objects, dates) which can't seed a simple filter.
   */
  filterValue?: string;
}

/**
 * Wrapper component for table cells that handles styling and layout.
 *
 * Emits `data-field-*` attributes so the row-level context menu (see TableRow)
 * can tell which cell was right-clicked and offer copy / filter-by-value
 * actions without wrapping every cell in its own menu.
 */
export function TableCellWrapper({
  fieldId,
  fieldConfig,
  cellIndex,
  totalCells,
  isTextareaField,
  columnStyle,
  children,
  fieldLabel,
  filterValue,
}: TableCellWrapperProps) {
  return (
    <div
      data-field-id={fieldId}
      data-field-label={fieldLabel}
      data-filter-value={filterValue}
      className={cn(
        "px-3 py-1.5 text-card-foreground text-xs border-r border-gray-100 h-full",
        isTextareaField ? "overflow-hidden" : "overflow-visible",
        // Primary (first) column pins past the 40px select cell so the row's
        // identity stays visible during horizontal scroll. bg-inherit matches
        // the row's striped/hover background and occludes scrolled content.
        cellIndex === 0 && "sticky left-10 z-raised bg-inherit border-r border-border",
        cellIndex === totalCells - 1 && "last:border-r-0"
      )}
      style={columnStyle}
    >
      {children}
    </div>
  );
}
